/**
 * Protocols of the link URLs the app may forward to the host and the host may open.
 * Shared by the app and the loader so both sides apply the same allowlist.
 */
export const OPENABLE_LINK_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"] as const;
export type OpenableLinkProtocol = (typeof OPENABLE_LINK_PROTOCOLS)[number];

const EXTERNAL_HANDLER_PROTOCOLS: ReadonlySet<OpenableLinkProtocol> = new Set(["mailto:", "tel:"]);

function isOpenableLinkProtocol(protocol: string): protocol is OpenableLinkProtocol {
  return (OPENABLE_LINK_PROTOCOLS as readonly string[]).includes(protocol);
}

/**
 * Returns the protocol of an absolute URL when it is allowlisted, `null` otherwise
 * (relative, malformed or non-allowlisted URL).
 */
export function getOpenableLinkProtocol(url: string): OpenableLinkProtocol | null {
  try {
    const { protocol } = new URL(url);
    return isOpenableLinkProtocol(protocol) ? protocol : null;
  } catch {
    return null;
  }
}

/** Whether the URL may be forwarded to and opened by the host. */
export function isOpenableLinkUrl(url: string): boolean {
  return getOpenableLinkProtocol(url) !== null;
}

/** Whether the protocol is handed to an external handler (mail client, dialer) instead of a new tab. */
export function isExternalHandlerProtocol(protocol: OpenableLinkProtocol): boolean {
  return EXTERNAL_HANDLER_PROTOCOLS.has(protocol);
}
